/**
 * KEY live re-apply service (frontend counterpart of the server-side
 * KeyHierarchyResolverBatch._applyKeyToLine math).
 *
 * WHY THIS EXISTS
 * ---------------
 * The API serves KEY-adjusted odds on GET /v3/events (server-side apply).
 * The socket, however, is platform-agnostic and sends RAW provider values on
 * every ODDS_CHANGE. If we just overwrite the store with the raw value, the
 * player sees a "leak" — a market that was balanced with a KEY suddenly
 * jumps to the unbalanced provider odds until the next full API refresh.
 *
 * Fix: after applying the socket update on `outcome_odd_original` (the raw
 * baseline stored on every outcome, sourced from the API response), we
 * re-run the same rescaling math client-side. Same invariants (blocked at
 * <= 1.01, provider-closed stays closed) and same safety-net check
 * (`keyLoosenLimitPp`) as the backend.
 *
 * PERFORMANCE
 * -----------
 * O(1) per outcome update (store lookup already O(1)) + O(M) per dirty
 * market where M = outcomes in the group (2-5 typical). Two lean passes
 * over `market.outcome` — NO intermediate array allocations, NO wrapper
 * objects, NO parseFloat when the raw is already a number. Store callers
 * MUST additionally guard `market.keyApplied != null` before adding to
 * their dirtyMarkets set — see the store-consumer notes (R.1 fix): early
 * bailout skips the function call entirely for the ~80% of markets that
 * carry no KEY rule.
 *
 * The service is stateless w.r.t. store data — it operates on references
 * the caller (store mutations) already holds. Only the skin-config is
 * cached here as a singleton, set once at boot by main.js after the
 * /v3/skins-config/platform-config response arrives.
 */

let _config = {
    enableKeyEvaluationLive: false,
    // K.4: loosen-limit safety net removed backend-side. The field is
    // kept in the config shape for backward-compat with the boot payload
    // but no longer read by reapplyKeyOnMarket (see the removed branch).
    keyLoosenLimitPp: null,
};

// Diagnostic flag — set `window.__DEBUG_KEY_LIVE_REAPPLY = true` in DevTools
// to see one log line per market processed. Reset with `= false` to silence.
// Off by default; zero cost when disabled (single boolean check).
function _debug(msg, extra) {
    if (typeof window !== 'undefined' && window.__DEBUG_KEY_LIVE_REAPPLY === true) {
        // eslint-disable-next-line no-console
        console.log('[keyLiveReapply]', msg, extra != null ? extra : '');
    }
}

/**
 * Called ONCE at app boot from main.js (applySkinSettings), and again on
 * any subsequent full skin-config refresh. The service reads these values
 * every time a socket update arrives — no need to plumb them through the
 * mutation chain.
 */
export function setKeyLiveConfig({ enableKeyEvaluationLive, keyLoosenLimitPp } = {}) {
    _config.enableKeyEvaluationLive = enableKeyEvaluationLive === true;
    // Explicit null/undefined check BEFORE Number(), because Number(null) === 0
    // and Number('') === 0 — both would be misread as "loosen cap = 0",
    // triggering the fallback on every rescale (the exact bug the null
    // fixture below unmasked). Only genuine finite numbers set a cap.
    if (keyLoosenLimitPp == null || keyLoosenLimitPp === '') {
        _config.keyLoosenLimitPp = null;
    } else {
        const n = Number(keyLoosenLimitPp);
        _config.keyLoosenLimitPp = Number.isFinite(n) ? n : null;
    }
}

/**
 * Re-applies KEY math on ONE market group after a socket odd update.
 *
 * The caller is expected to have already:
 *   1. Located the market and its outcome via an O(1) lookup on unique_id
 *   2. Updated `outcome.outcome_odd_original` = parseFloat(socket.attributes.odds)
 *   3. Updated `outcome.status` / `outcome.active` from the socket flags
 *
 * This function then rescales EVERY outcome of the group so the aggregate
 * book equals `market.keyApplied`. It mutates the outcomes in place — the
 * caller passes a reference to the store object, so Vue reactivity picks
 * up the change automatically (both Vue 2 and 3, provided the field
 * existed at initial render time).
 *
 * Semantics matching backend `_applyKeyToLine`:
 *   - Only outcomes with `active === true` and a positive `outcome_odd_original`
 *     participate in the sum
 *   - Rescaling formula:
 *         keyPercent    = sum(100 / oddOriginal_i)   (over active outcomes)
 *         multiplier    = keyPercent / targetKey
 *         newOdd_i      = round2(oddOriginal_i * multiplier)
 *   - Hard invariants:
 *         · provider-closed (active=false) stays closed
 *         · if newOdd <= 1.01 → mark active=false + blockedByKey=true
 *   - Safety net: if `keyPercent - targetKey > keyLoosenLimitPp` → skip
 *     rescale, restore raw values, mark market.keyFallback for debug.
 *
 * @param {Object} market - store reference; expected fields:
 *                          { outcome[], keyApplied, keyFallback, unique_id }
 * @returns {boolean}     true if a rescale was performed, false otherwise.
 */
export function reapplyKeyOnMarket(market) {
    if (!_config.enableKeyEvaluationLive) {
        _debug('SKIP — enableKeyEvaluationLive=false');
        return false;
    }
    if (!market || !Array.isArray(market.outcome)) {
        _debug('SKIP — no market or no outcome array');
        return false;
    }

    const targetKey = Number(market.keyApplied);
    if (!Number.isFinite(targetKey) || targetKey <= 0) {
        _debug('SKIP — invalid keyApplied', market.keyApplied);
        return false;
    }

    const outcomes = market.outcome;
    const len = outcomes.length;

    // ── Pass 1 — book computation (allocation-free) ─────────────────────
    // K.11 (SHADOW-ODDS BOOK) — every outcome with a valid raw contributes
    // to the book, regardless of `active` state. A provider-suspended
    // outcome's raw is a "shadow odd" for the book — it does NOT get
    // rescaled visibly (see Pass 2, the guard skips outcomes that were
    // never active), but its 100/raw is part of the natural book so the
    // multiplier stays consistent with what rocky-api computes server-side.
    //
    // Historical note: K.9 (2026-07-23) had introduced a participating
    // filter (exclude active=false && !blockedByKey) at this point. That
    // change over-tightened the multiplier when the provider suspended
    // an outcome and cascade-locked the whole market via the 1.01 floor.
    // Reverted in K.11 (2026-07-24) to match the pre-K.9 shadow-odds
    // behaviour that rocky-api / rocky-bets already implement.
    //
    // No `participating[]` array allocation — two lean passes over the
    // outcomes array. On mobile the GC pressure of wrapper objects was
    // measurable in flame charts.
    let keyPercent = 0;
    let contributedCount = 0;
    for (let i = 0; i < len; i++) {
        const o = outcomes[i];
        const raw = o.outcome_odd_original;
        // No parseFloat — the socket handlers already store numbers here.
        // Defensive guard for older code paths that might set strings.
        const rawNum = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(rawNum) || rawNum <= 0) continue;
        keyPercent += 100 / rawNum;
        contributedCount++;
    }
    if (contributedCount === 0 || keyPercent === 0) {
        _debug('SKIP — no valid raw odds in the market', {
            marketId: market.id || market.unique_id,
            outcomesCount: len
        });
        return false;
    }
    _debug('rescale', {
        marketId: market.id || market.unique_id,
        targetKey,
        keyPercent: Number(keyPercent.toFixed(2)),
        multiplier: Number((keyPercent / targetKey).toFixed(4)),
        contributed: contributedCount
    });

    // K.4: the previous loosen-limit fallback (server-side + client-side)
    // was removed. The client no longer restores raw display values when
    // the target diverges hard from the natural book — the admin owns
    // the responsibility for a sane target. Any residual `keyFallback`
    // on the market from an older payload is cleared below on rescale.

    // ── Pass 2 — rescale (allocation-free) ─────────────────────────────
    // K.11 — every outcome with a valid raw gets its `outcome_odd_value`
    // updated. Provider-closed outcomes have their `active` untouched
    // (they stay closed). Provider-open outcomes whose rescaled value
    // falls ≤ 1.01 get auto-locked via blockedByKey — granular, only
    // that single outcome, not the whole market.
    const multiplier = keyPercent / targetKey;
    for (let i = 0; i < len; i++) {
        const o = outcomes[i];
        const rawNum = typeof o.outcome_odd_original === 'number'
            ? o.outcome_odd_original : Number(o.outcome_odd_original);
        if (!Number.isFinite(rawNum) || rawNum <= 0) continue;

        // 2-decimal rounding matches the backend's Number(newOdd.toFixed(2)).
        const rounded = Math.round(rawNum * multiplier * 100) / 100;
        o.outcome_odd_value = rounded;

        // Per-outcome status transitions. Provider-closed (active !== true
        // AND not previously blocked by KEY) is NEVER re-opened here —
        // we only manage the outcome's own state for the two cases
        // where WE own the block.
        //
        // Floor: `< 1.01` blocks (≤ 1.00), 1.01 itself is a valid quote.
        if (o.active === true) {
            if (rounded < 1.01) {
                o.active = false;
                o.blockedByKey = true;
            } else if (o.blockedByKey === true) {
                // Was blocked by KEY in a previous rescale; the socket
                // mutation flipped active=true first and now the fresh
                // multiplier yields a healthy value → drop the flag.
                o.blockedByKey = false;
            }
        } else if (o.blockedByKey === true) {
            // active=false + blockedByKey=true → previously our block.
            // Fresh rescale healthy → unblock the outcome.
            if (rounded >= 1.01) {
                o.active = true;
                o.blockedByKey = false;
            }
        }
        // else: provider-closed genuinely (active=false, blockedByKey!=true)
        //       → leave the flags alone. `outcome_odd_value` was still
        //       updated above for display consistency with the server.
    }
    market.keyFallback = null;
    return true;
}

/**
 * Batch entry point for socket mutations. Given a dirty market set collected
 * during the socket-update loop, replays the KEY math on each. Deduped by
 * caller (Set), so each market is processed exactly once regardless of how
 * many outcomes of it changed in the same batch.
 *
 * @param {Iterable<Object>} markets
 * @returns {{ processed: number, rescaled: number, fallback: number }}
 */
export function reapplyKeyOnMarkets(markets) {
    const report = { processed: 0, rescaled: 0, fallback: 0 };
    for (const m of markets) {
        report.processed++;
        const before = m.keyFallback;
        const did = reapplyKeyOnMarket(m);
        if (did) report.rescaled++;
        else if (m.keyFallback && before !== m.keyFallback) report.fallback++;
    }
    return report;
}

/**
 * Test hook — exposes the internal config so unit tests can verify the
 * boot wiring without going through the whole Vuex round-trip. NOT for
 * production callers.
 */
export function _getConfigForTest() {
    return { ..._config };
}

/**
 * Simulator — computes what a market would look like under a HYPOTHETICAL
 * target key, WITHOUT mutating the store. Used by the debug inspector modal
 * ("what if I applied key=110 instead of 100 to this market?").
 *
 * Same math as `reapplyKeyOnMarket` but pure: reads `outcome_odd_original`
 * from each outcome and returns a new array of simulated results. The
 * store objects are NEVER touched.
 *
 * Ignores the `_config.enableKeyEvaluationLive` flag — the debug UI wants to
 * see hypothetical outcomes regardless of the runtime feature toggle.
 *
 * K.4: the loosen-limit safety net was removed — the simulator now always
 * shows the rescaled outcomes. The `fallbackReason` return field is kept
 * for backward-compat with the KeyInspector modal (always null).
 *
 * @param {Object} market — same shape as reapplyKeyOnMarket expects
 * @param {number} hypotheticalKey — the target key to simulate
 * @param {Object} [_options]  — accepted-but-ignored; kept for signature parity.
 * @returns {{
 *     targetKey: number,
 *     providerBook: number,
 *     multiplier: number,
 *     fallbackReason: null,
 *     outcomes: Array<{
 *         id: string,
 *         name: string,
 *         raw: number,
 *         simulated: number,
 *         wouldBeBlocked: boolean,
 *         providerActive: boolean
 *     }>
 * }|null}
 *   Returns null when the market is empty, has no valid raw values, or the
 *   hypothetical key is invalid.
 */
// eslint-disable-next-line no-unused-vars
export function simulateKeyOnMarket(market, hypotheticalKey, _options = {}) {
    if (!market || !Array.isArray(market.outcome)) return null;
    const targetKey = Number(hypotheticalKey);
    if (!Number.isFinite(targetKey) || targetKey <= 0) return null;

    // K.11 — SHADOW-ODDS BOOK. Every outcome with a valid raw contributes
    // to the book, regardless of active state. `providerActive` on the
    // per-outcome return preserves the provider-closed flag so the UI
    // can render "closed" chips without confusing the simulator math.
    const outcomes = market.outcome;
    const contributing = [];
    let providerBook = 0;
    for (let i = 0; i < outcomes.length; i++) {
        const o = outcomes[i];
        const raw = parseFloat(o.outcome_odd_original);
        if (!Number.isFinite(raw) || raw <= 0) continue;
        contributing.push({ o, raw });
        providerBook += 100 / raw;
    }
    if (contributing.length === 0 || providerBook === 0) return null;

    const multiplier = providerBook / targetKey;

    const simulatedOutcomes = contributing.map(({ o, raw }) => {
        const simulated = Math.round(raw * multiplier * 100) / 100;
        const providerActive = o.active === true;
        return {
            id: String(o.unique_id ?? o.outcome_id ?? o.id ?? ''),
            name: o.outcome_alias || o.name || o.outcomeName || '',
            raw,
            simulated,
            // `wouldBeBlocked` fires only for provider-active outcomes
            // that would fall STRICTLY below 1.01 (i.e. ≤ 1.00) after
            // rescale. 1.01 itself is a valid quote. A provider-closed
            // outcome stays closed regardless.
            wouldBeBlocked: providerActive && simulated < 1.01,
            providerActive
        };
    });

    return {
        targetKey,
        providerBook: Number(providerBook.toFixed(2)),
        multiplier: Number(multiplier.toFixed(4)),
        fallbackReason: null,
        outcomes: simulatedOutcomes
    };
}
